// ============================================================
// Génération de minerais, rejouée à l'identique de ce que le mod posera
// ============================================================
// Repris de l'aperçu de NG Java (NGConfigAPI, frontend/src/lib/oregen/oreGenerator.js et oreConfig.js),
// miroir de la passe oregen de NGWorldEdit ; WorldOriginMod (Ores/OreGenerator.cs) rejoue le même
// algorithme. Les trois sont tenus ensemble par un vecteur de parité (parity-vector.json, le même
// fichier dans les trois dépôts) : voir minerais.test.ts. Toute divergence est un bug.
//
// Le contenu d'un chunk ne dépend que de ses coordonnées : pour remplir un chunk, on rejoue aussi ses
// voisins (un filon déborde) et on ne garde que ce qui tombe chez lui. Aucune fonction trigonométrique
// (Math.sin n'est spécifié qu'au dernier bit près) : direction par racine carrée, atténuation par une
// parabole, le reste en flottants doubles, identiques en Java, en C# et ici.
// ============================================================

import { JavaRandom, chunkSeed, oreSeed } from './javaRandom.ts';

export const WORLD_FLOOR_Y = -64;
export const WORLD_CEILING_Y = 319;
export const MAX_VEIN_SIZE = 128;
const MAX_JAVA_BLOCK_ID = 4095;

export type OreDist = 'uniform' | 'triangle' | 'trapezoid';
export type OreShape = 'blob' | 'scatter';

/** Une ligne de minerai prête pour le générateur (bornes appliquées, plage croisée avec sa couche). */
export interface GenOre {
  layerIndex: number;
  oreIndex: number;
  name: string;
  /** Bloc posé : identifiant Bedrock (WorldOriginMod) ou numéro Java (vecteur de parité). */
  block: string;
  /** Métadonnée Java (vecteur de parité) ; 0 pour WorldOriginMod. */
  meta: number;
  attempts: number;
  sizeMin: number;
  sizeMax: number;
  dist: OreDist;
  plateau: number;
  minY: number;
  maxY: number;
  shape: OreShape;
  scatterSpread: number;
  /** Tire quelque chose : tentatives, plage non vide, bloc connu. Une ligne inactive garde son rang. */
  active: boolean;
}

export interface GenLayer {
  name: string;
  minY: number;
  maxY: number;
  /** Roche de la couche (fond de la coupe). */
  fill: string;
  ores: GenOre[];
}

export interface GenConfig {
  seed: bigint;
  layers: GenLayer[];
  activeOres: GenOre[];
  neighbourRadius: number;
}

/** Appel du générateur pour chaque bloc posé dans le chunk visé (doublons compris, dans l'ordre du mod). */
export type OreSink = (x: number, y: number, z: number, ore: GenOre) => void;

export const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
export const clampY = (y: number) => clamp(y, WORLD_FLOOR_Y, WORLD_CEILING_Y);

/**
 * Portée horizontale maximale d'un dépôt, en blocs, majorée comme côté mod : se tromper vers le haut
 * coûte un peu de calcul, vers le bas fait disparaître des blocs en bordure de chunk.
 */
function reachOf(ore: GenOre): number {
  if (ore.shape === 'scatter') return ore.scatterSpread + 1;
  return Math.trunc(ore.sizeMax / 8) + Math.ceil(ore.sizeMax / 16) + 2;
}

/** Lignes actives (les mêmes objets que ceux des couches) et rayon des voisins à rejouer. */
export function sealConfig(seed: bigint, layers: GenLayer[]): GenConfig {
  const activeOres: GenOre[] = [];
  let maxReach = 0;
  for (const layer of layers) {
    for (const ore of layer.ores) {
      if (!ore.active) continue;
      activeOres.push(ore);
      maxReach = Math.max(maxReach, reachOf(ore));
    }
  }
  return { seed, layers, activeOres, neighbourRadius: Math.max(1, Math.floor((maxReach + 15) / 16)) };
}

/**
 * Tous les blocs de minerai qui atterrissent dans le chunk (chunkX, chunkZ), dans l'ordre où le mod
 * les pose (chunks sources de -r à r, couches puis lignes), doublons compris.
 */
export function generateChunk(config: GenConfig, chunkX: number, chunkZ: number, sink: OreSink): void {
  if (config.activeOres.length === 0) return;
  const radius = config.neighbourRadius;
  const minX = chunkX << 4;
  const minZ = chunkZ << 4;
  const box = { minX, minZ, maxX: minX + 15, maxZ: minZ + 15 };
  for (let dx = -radius; dx <= radius; dx += 1) {
    for (let dz = -radius; dz <= radius; dz += 1) {
      sourceChunk(config, chunkX + dx, chunkZ + dz, box, sink);
    }
  }
}

interface Box { minX: number; minZ: number; maxX: number; maxZ: number }

/** Rejoue un chunk source entier (le flux de tirages n'en dépend pas du chunk visé) et garde la fenêtre. */
function sourceChunk(config: GenConfig, sourceX: number, sourceZ: number, box: Box, sink: OreSink): void {
  const seed = chunkSeed(config.seed, sourceX, sourceZ);
  for (let layerIndex = 0; layerIndex < config.layers.length; layerIndex += 1) {
    const ores = config.layers[layerIndex].ores;
    for (let oreIndex = 0; oreIndex < ores.length; oreIndex += 1) {
      const ore = ores[oreIndex];
      // Une ligne inactive ne consomme aucun tirage : son rang isole les autres, la couper ne déplace rien.
      if (!ore.active) continue;
      const random = new JavaRandom(oreSeed(seed, layerIndex, oreIndex));
      deposits(ore, random, sourceX, sourceZ, box, sink);
    }
  }
}

function deposits(ore: GenOre, random: JavaRandom, sourceX: number, sourceZ: number, box: Box, sink: OreSink): void {
  let count = Math.trunc(ore.attempts);
  const fraction = ore.attempts - count;
  // La partie fractionnaire est une probabilité, tirée dès qu'elle est non nulle.
  if (fraction > 0 && random.nextFloat() < fraction) count += 1;
  for (let i = 0; i < count; i += 1) {
    if (ore.shape === 'scatter') scatter(ore, random, sourceX, sourceZ, box, sink);
    else blob(ore, random, sourceX, sourceZ, box, sink);
  }
}

/** Filon ellipsoïdal le long d'un axe ; tous les tirages avant la géométrie, dans l'ordre fixé. */
function blob(ore: GenOre, random: JavaRandom, sourceX: number, sourceZ: number, box: Box, sink: OreSink): void {
  const originX = (sourceX << 4) + random.nextInt(16);
  const originZ = (sourceZ << 4) + random.nextInt(16);
  const originY = pickY(ore, random);
  const size = pickSize(ore, random);

  const dirX = random.nextFloat() * 2.0 - 1.0;
  const negative = random.nextInt(2) === 1;
  let dirZ = Math.sqrt(Math.max(0.0, 1.0 - dirX * dirX));
  if (negative) dirZ = -dirZ;

  const deltaY1 = random.nextInt(3) - 2;
  const deltaY2 = random.nextInt(3) - 2;

  const radii = new Float64Array(size);
  for (let i = 0; i < size; i += 1) radii[i] = random.nextFloat();

  const spread = size / 8.0;
  const offsetX = dirX * spread;
  const offsetZ = dirZ * spread;
  const x1 = originX + offsetX;
  const x2 = originX - offsetX;
  const z1 = originZ + offsetZ;
  const z2 = originZ - offsetZ;
  const y1 = originY + deltaY1;
  const y2 = originY + deltaY2;

  const maxRadius = ((2.0 * size) / 16.0 + 1.0) / 2.0;
  if (Math.min(x1, x2) - maxRadius > box.maxX || Math.max(x1, x2) + maxRadius < box.minX
    || Math.min(z1, z2) - maxRadius > box.maxZ || Math.max(z1, z2) + maxRadius < box.minZ) {
    return; // Dépôt hors fenêtre : ses tirages sont déjà consommés.
  }

  for (let i = 0; i < size; i += 1) {
    const t = i / size;
    const centerX = x1 + (x2 - x1) * t;
    const centerY = y1 + (y2 - y1) * t;
    const centerZ = z1 + (z2 - z1) * t;
    const taper = 4.0 * t * (1.0 - t);
    const radius = ((taper + 1.0) * radii[i] * size / 16.0 + 1.0) / 2.0;
    sphere(ore, centerX, centerY, centerZ, radius, box, sink);
  }
}

/** Une bille de l'axe du filon ; son bloc central est toujours posé (un rayon sous un demi-bloc n'en couvre aucun). */
function sphere(ore: GenOre, centerX: number, centerY: number, centerZ: number, radius: number, box: Box, sink: OreSink): void {
  const seedX = Math.floor(centerX);
  const seedY = Math.floor(centerY);
  const seedZ = Math.floor(centerZ);
  if (seedX >= box.minX && seedX <= box.maxX && seedZ >= box.minZ && seedZ <= box.maxZ
    && seedY >= WORLD_FLOOR_Y && seedY <= WORLD_CEILING_Y) {
    sink(seedX, seedY, seedZ, ore);
  }

  const fromX = Math.max(box.minX, Math.floor(centerX - radius));
  const toX = Math.min(box.maxX, Math.floor(centerX + radius));
  const fromZ = Math.max(box.minZ, Math.floor(centerZ - radius));
  const toZ = Math.min(box.maxZ, Math.floor(centerZ + radius));
  const fromY = Math.max(WORLD_FLOOR_Y, Math.floor(centerY - radius));
  const toY = Math.min(WORLD_CEILING_Y, Math.floor(centerY + radius));

  for (let x = fromX; x <= toX; x += 1) {
    const dx = (x + 0.5 - centerX) / radius;
    const dx2 = dx * dx;
    if (dx2 >= 1.0) continue;
    for (let z = fromZ; z <= toZ; z += 1) {
      const dz = (z + 0.5 - centerZ) / radius;
      const dxz2 = dx2 + dz * dz;
      if (dxz2 >= 1.0) continue;
      for (let y = fromY; y <= toY; y += 1) {
        const dy = (y + 0.5 - centerY) / radius;
        if (dxz2 + dy * dy < 1.0) sink(x, y, z, ore);
      }
    }
  }
}

/** Dépôt dispersé : des blocs isolés autour d'un point. */
function scatter(ore: GenOre, random: JavaRandom, sourceX: number, sourceZ: number, box: Box, sink: OreSink): void {
  const originX = (sourceX << 4) + random.nextInt(16);
  const originZ = (sourceZ << 4) + random.nextInt(16);
  const originY = pickY(ore, random);
  const size = pickSize(ore, random);
  const spread = ore.scatterSpread;
  const span = spread * 2 + 1;
  for (let i = 0; i < size; i += 1) {
    const x = originX + random.nextInt(span) - spread;
    const y = originY + random.nextInt(span) - spread;
    const z = originZ + random.nextInt(span) - spread;
    if (x < box.minX || x > box.maxX || z < box.minZ || z > box.maxZ) continue;
    if (y < WORLD_FLOOR_Y || y > WORLD_CEILING_Y) continue;
    sink(x, y, z, ore);
  }
}

function pickSize(ore: GenOre, random: JavaRandom): number {
  return ore.sizeMax > ore.sizeMin ? ore.sizeMin + random.nextInt(ore.sizeMax - ore.sizeMin + 1) : ore.sizeMin;
}

/** Altitude d'un dépôt : uniforme, triangle (plateau nul) ou trapèze (plateau central, deux rampes). */
function pickY(ore: GenOre, random: JavaRandom): number {
  const span = ore.maxY - ore.minY;
  if (span <= 0) return ore.minY;
  if (ore.dist === 'uniform') return ore.minY + random.nextInt(span + 1);
  const plateau = ore.dist === 'triangle' ? 0 : ore.plateau;
  if (plateau >= span) return ore.minY + random.nextInt(span + 1);
  const lower = Math.trunc((span - plateau) / 2);
  const upper = span - lower;
  return ore.minY + random.nextInt(upper + 1) + random.nextInt(lower + 1);
}

/** Poses d'un chunk sous forme canonique (triées, dédoublonnées), celle du vecteur de parité. */
export function canonicalPlacements(config: GenConfig, chunkX: number, chunkZ: number): string[] {
  const out: string[] = [];
  generateChunk(config, chunkX, chunkZ, (x, y, z, ore) => { out.push(`${x},${y},${z},${ore.block},${ore.meta}`); });
  out.sort();
  const unique: string[] = [];
  let previous: string | null = null;
  for (const entry of out) {
    if (entry !== previous) {
      unique.push(entry);
      previous = entry;
    }
  }
  return unique;
}

export function parseDist(raw: unknown): OreDist {
  const value = String(raw ?? '').trim().toLowerCase();
  if (value === 'triangle') return 'triangle';
  if (value === 'trapezoid' || value === 'trapeze') return 'trapezoid';
  return 'uniform';
}

export function parseShape(raw: unknown): OreShape {
  return String(raw ?? '').trim().toLowerCase() === 'scatter' ? 'scatter' : 'blob';
}

// ==================== Format de NG Java (vecteur de parité) ====================

type JavaRaw = Record<string, unknown>;

const intOr = (raw: unknown, fallback: number) => {
  const n = Number(raw);
  return raw !== null && raw !== undefined && raw !== '' && Number.isFinite(n) ? Math.trunc(n) : fallback;
};

const numberOr = (raw: unknown, fallback: number) => {
  const n = Number(raw);
  return raw !== null && raw !== undefined && raw !== '' && Number.isFinite(n) ? n : fallback;
};

/** Graine lue sans passer par Number (64 bits). */
export function toSeed(raw: unknown): bigint {
  if (typeof raw === 'bigint') return BigInt.asIntN(64, raw);
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return /^-?\d+$/.test(trimmed) ? BigInt.asIntN(64, BigInt(trimmed)) : 0n;
  }
  const n = Number(raw);
  return Number.isFinite(n) ? BigInt.asIntN(64, BigInt(Math.trunc(n))) : 0n;
}

/**
 * Config au format kebab-case du panel NG Java (celui du vecteur de parité), avec les bornes et les
 * défauts de NGWorldEdit (OreConfig.parse) : sert à rejouer le vecteur.
 */
export function parseJavaOreConfig(raw: JavaRaw, worldSeed = 0n): GenConfig {
  const declared = toSeed(raw.seed);
  const seed = declared !== 0n ? declared : BigInt.asIntN(64, worldSeed);
  const layers: GenLayer[] = (Array.isArray(raw.layers) ? raw.layers as JavaRaw[] : []).map((layerRaw, layerIndex) => {
    const source = layerRaw ?? {};
    let minY = clampY(intOr(source['min-y'], WORLD_FLOOR_Y));
    let maxY = clampY(intOr(source['max-y'], WORLD_CEILING_Y));
    if (minY > maxY) [minY, maxY] = [maxY, minY];
    const name = String(source.name ?? `Couche ${layerIndex + 1}`);
    const ores = (Array.isArray(source.ores) ? source.ores as JavaRaw[] : []).map((oreRaw, oreIndex): GenOre => {
      const o = oreRaw ?? {};
      let block = intOr(o.block, 0);
      if (block < 0 || block > MAX_JAVA_BLOCK_ID) block = 0;
      const sizeMin = clamp(intOr(o['size-min'], 8), 1, MAX_VEIN_SIZE);
      const sizeMax = clamp(intOr(o['size-max'], sizeMin), sizeMin, MAX_VEIN_SIZE);
      const dist = parseDist(o.dist);
      const oreMinY = Math.max(minY, clampY(intOr(o['min-y'], minY)));
      const oreMaxY = Math.min(maxY, clampY(intOr(o['max-y'], maxY)));
      const attempts = clamp(numberOr(o.attempts, 0), 0, 4096);
      return {
        layerIndex,
        oreIndex,
        name: String(o.name ?? `minerai${oreIndex}`),
        block: String(block),
        meta: clamp(intOr(o.meta, 0), 0, 15),
        attempts,
        sizeMin,
        sizeMax,
        dist,
        plateau: dist === 'triangle' ? 0 : clamp(intOr(o.plateau, 0), 0, Math.max(0, oreMaxY - oreMinY)),
        minY: oreMinY,
        maxY: oreMaxY,
        shape: parseShape(o.shape),
        scatterSpread: clamp(intOr(o['scatter-spread'], 6), 1, 32),
        active: attempts > 0 && oreMinY <= oreMaxY && block > 0,
      };
    });
    return { name, minY, maxY, fill: String(source['fill-block'] ?? '1'), ores };
  });
  return sealConfig(seed, layers);
}
